import { z } from "zod";

import {
  CreateCustomFieldRequest,
  CustomFieldType,
  TargetEntityType,
} from "@/shared/domain/customFields";

import { descriptionSchema, displayNameSchema } from "./customFieldFormShared";

/**
 * Mirrors the SDK's CUSTOM_FIELD_NAME_PATTERN
 * (packages/roboto/src/roboto/domain/custom_fields/record.py): a lowercase
 * ASCII letter followed by up to 62 lowercase letters, digits, or underscores.
 */
export const FIELD_NAME_REGEX = /^[a-z][a-z0-9_]{0,62}$/;

const MAX_ENUM_VALUE_LENGTH = 256;

/** Field types offered by the create form, in the designer's grid order. */
export const CREATE_FIELD_TYPE_OPTIONS: CustomFieldType[] = [
  CustomFieldType.String,
  CustomFieldType.Number,
  CustomFieldType.Enum,
  CustomFieldType.Boolean,
  CustomFieldType.Timestamp,
];

export const DEFAULT_CREATE_CUSTOM_FIELD_VALUES: CreateCustomFieldFormValues = {
  display_name: "",
  field_name: "",
  description: "",
  field_type: CustomFieldType.String,
  enum_values: [{ value: "" }],
};

export const createCustomFieldFormSchema = z
  .object({
    display_name: displayNameSchema,
    field_name: z
      .string()
      .min(1, "Field name is required.")
      .regex(
        FIELD_NAME_REGEX,
        "Start with a lowercase letter; use only lowercase letters, digits, and underscores (max 63).",
      ),
    description: descriptionSchema,
    field_type: z.enum(CustomFieldType),
    // One entry per editor row; useFieldArray needs an object per item. Empty
    // rows are ignored on submit.
    enum_values: z.array(z.object({ value: z.string() })),
  })
  .superRefine((values, ctx) => {
    if (values.field_type !== CustomFieldType.Enum) {
      return;
    }

    // Enum fields need at least one allowed value; values must be unique and
    // within length. Blank rows are ignored (dropped on submit), so a trailing
    // empty row never blocks submission.
    const seen = new Set<string>();
    let nonEmptyCount = 0;

    values.enum_values.forEach((option, index) => {
      const trimmed = option.value.trim();
      if (trimmed.length === 0) {
        return;
      }

      nonEmptyCount += 1;

      if (trimmed.length > MAX_ENUM_VALUE_LENGTH) {
        ctx.addIssue({
          code: "custom",
          message: `Option must be ${MAX_ENUM_VALUE_LENGTH} characters or fewer.`,
          path: ["enum_values", index, "value"],
        });
      }

      if (seen.has(trimmed)) {
        ctx.addIssue({
          code: "custom",
          message: "Duplicate option.",
          path: ["enum_values", index, "value"],
        });
      }
      seen.add(trimmed);
    });

    if (nonEmptyCount === 0) {
      ctx.addIssue({
        code: "custom",
        message: "Add at least one option.",
        path: ["enum_values", 0, "value"],
      });
    }
  });

/** Inferred from the schema so the form's value type can't drift from validation. */
export type CreateCustomFieldFormValues = z.infer<
  typeof createCustomFieldFormSchema
>;

/**
 * Maps validated form values to the create request: trims and drops empty
 * optional text fields, and assembles enum options (non-empty, trimmed) only
 * for enum fields.
 */
export function buildCreateCustomFieldRequest(
  values: CreateCustomFieldFormValues,
  entityType: TargetEntityType,
): CreateCustomFieldRequest {
  const displayName = values.display_name.trim();
  const description = values.description.trim();

  const request: CreateCustomFieldRequest = {
    entity_type: entityType,
    field_name: values.field_name,
    field_type: values.field_type,
  };

  if (displayName.length > 0) {
    request.display_name = displayName;
  }
  if (description.length > 0) {
    request.description = description;
  }
  if (values.field_type === CustomFieldType.Enum) {
    request.options = {
      field_type: CustomFieldType.Enum,
      enum_values: values.enum_values
        .map((option) => option.value.trim())
        .filter((value) => value.length > 0),
    };
  }

  return request;
}
